import { TextField, Typography } from '@mui/material';
import Dropdown, { createDropdownItem, DropdownItem } from 'components/Dropdown/Dropdown';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type LocationSelectorProps = {
  city?: string;
  province?: string;
  provinces: string[];
  onProvinceChanged: (province: string) => void;
  onCityChanged: (city: string) => void;
  disabled?: boolean;
  labelAbove?: boolean;
};

export const NO_PROVINCE_OPTION = 'LocationSelector__NO_PROVINCE_OPTION';

export const LocationSelector = (props: LocationSelectorProps) => {
  const { city, province, provinces, onProvinceChanged, onCityChanged, disabled, labelAbove } = props;

  const { t } = useTranslation();
  const noProvinceOption = { title: t('components.product-edit.no-province'), value: NO_PROVINCE_OPTION };

  const selectedProvince = useMemo(() => {
    return province ? createDropdownItem(province) : noProvinceOption;
  }, [province]);

  const provinceOptions = [noProvinceOption, ...provinces.map((p) => ({ title: p, value: p }))];

  const provinceChanged = (selected: DropdownItem) => {
    if (selected.value === NO_PROVINCE_OPTION) {
      onProvinceChanged(null);
    } else {
      onProvinceChanged(selected.title);
    }
  };

  const cityChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const city = e.target.value;

    onCityChanged(city);
    e.preventDefault();
  };

  return (
    <>
      <div className="mt-2">
        <Dropdown
          label={`${t('components.product-edit.province')} (${t('components.product-edit.optional')})`}
          labelAbove={labelAbove}
          selectedItem={selectedProvince}
          translateItems={false}
          items={provinceOptions}
          onSelect={provinceChanged}
          disabled={disabled}
          placeholder={t('components.product-edit.placeholders.province')}
          deSelectable={true}
          textFieldProps={
            !labelAbove
              ? {
                  InputLabelProps: {
                    shrink: true,
                    className: 'product-details-input-label',
                  },
                }
              : undefined
          }
        />
      </div>

      {/* CITY */}
      <div className="mt-2">
        {labelAbove && (
          <Typography variant="subtitle1" className="pb-1">
            {`${t('components.product-edit.city')} (${t('components.product-edit.optional')})`}
          </Typography>
        )}
        <TextField
          value={city}
          variant={labelAbove ? 'outlined' : 'standard'}
          size={labelAbove ? 'small' : undefined}
          fullWidth={true}
          label={!labelAbove ? `${t('components.product-edit.city')} (${t('components.product-edit.optional')})` : undefined}
          placeholder={t('components.product-edit.specific-location')}
          color="primary"
          disabled={disabled}
          multiline
          onChange={cityChanged}
          InputLabelProps={
            !labelAbove
              ? {
                  shrink: true,
                  className: 'product-details-input-label',
                }
              : undefined
          }
        />
      </div>
    </>
  );
};
